import { useCallback, useEffect } from 'react';
import { useFetcher } from 'react-router';

import { useBloomBasename } from '@jane/bloom/hooks/useBloomBasename';
import { useHardRedirect } from '@jane/bloom/hooks/useHardRedirect';
import { useRootLoaderData } from '@jane/bloom/hooks/useRootLoaderData';
import type {
  Payload,
  SignupUserResponse,
} from '@jane/bloom/routes/$basename.api.signup/types';
import { trackRegistered } from '@jane/bloom/services/tracking/track';
import { buildFormData } from '@jane/bloom/utils/formData';

interface UseSignupArgs {
  skipBagAfterAuth?: boolean;
}

export const useSignup = ({ skipBagAfterAuth }: UseSignupArgs = {}) => {
  const { featureFlags, googleSsoClientId } = useRootLoaderData();
  const basename = useBloomBasename();
  const hardRedirect = useHardRedirect();
  const fetcher = useFetcher<SignupUserResponse>();

  const signup = useCallback(
    (args: Omit<Payload, 'skipBag'>) => {
      return fetcher.submit(
        buildFormData({
          ...args,
          skipBag: skipBagAfterAuth ? `${skipBagAfterAuth}` : undefined,
        }),
        {
          action: `${basename}api/signup`,
          method: 'post',
          preventScrollReset: true,
        }
      );
    },
    [basename, fetcher, skipBagAfterAuth]
  );

  useEffect(() => {
    if (fetcher.state === 'idle' && fetcher.data?.success) {
      trackRegistered(
        {
          googleSsoEnabled: !!googleSsoClientId,
          method: 'email',
          source: fetcher.data.payload.authSource,
        },
        featureFlags
      );
      hardRedirect(fetcher.data.payload.redirectTo);
    }
  }, [
    featureFlags,
    fetcher.state,
    fetcher.data,
    googleSsoClientId,
    hardRedirect,
  ]);

  return {
    fetcher,
    signup,
  };
};
